// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import Users from '@app/features/user/state/Users';
import {makeAutoObservable} from 'mobx';

export interface ModNote {
	id: string;
	guildId: string;
	targetUserId: string;
	authorId: string;
	authorTag: string;
	text: string;
	createdAt: number;
}

const STORAGE_PREFIX = 'fluxer:modnotes:';

function getStorageKey(guildId: string, userId: string): string {
	return `${STORAGE_PREFIX}${guildId}:${userId}`;
}

class ModNotesStore {
	private notesCache = new Map<string, Array<ModNote>>();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	getNotes(guildId: string, userId: string): Array<ModNote> {
		const key = getStorageKey(guildId, userId);
		if (this.notesCache.has(key)) {
			return this.notesCache.get(key)!;
		}

		let notes: Array<ModNote> = [];
		if (typeof window !== 'undefined') {
			try {
				const saved = localStorage.getItem(key);
				if (saved) {
					const parsed = JSON.parse(saved);
					if (Array.isArray(parsed)) {
						notes = parsed;
					}
				}
			} catch {
				// Ignore parse error
			}
		}

		this.notesCache.set(key, notes);
		return notes;
	}

	addNote(guildId: string, targetUserId: string, text: string): ModNote {
		const authorId = Authentication.currentUserId ?? 'unknown';
		const authorUser = Users.getUser(authorId);
		const authorTag = authorUser?.tag || authorUser?.username || 'Staff Member';

		const newNote: ModNote = {
			id: `note-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
			guildId,
			targetUserId,
			authorId,
			authorTag,
			text: text.trim(),
			createdAt: Date.now(),
		};

		const key = getStorageKey(guildId, targetUserId);
		const current = this.getNotes(guildId, targetUserId);
		const updated = [newNote, ...current];

		this.notesCache.set(key, updated);
		if (typeof window !== 'undefined') {
			try {
				localStorage.setItem(key, JSON.stringify(updated));
			} catch {
				// Storage full or unavailable
			}
		}

		return newNote;
	}

	deleteNote(guildId: string, targetUserId: string, noteId: string): void {
		const key = getStorageKey(guildId, targetUserId);
		const current = this.getNotes(guildId, targetUserId);
		const updated = current.filter((n) => n.id !== noteId);

		this.notesCache.set(key, updated);
		if (typeof window !== 'undefined') {
			try {
				localStorage.setItem(key, JSON.stringify(updated));
			} catch {
				// Storage full or unavailable
			}
		}
	}
}

const instance = new ModNotesStore();
export default instance;
